import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ErrorBoundary } from 'react-error-boundary';

// Providers
import { AuthProvider } from '@/features/auth/context/AuthContext';
import { ThemeProvider } from '@/contexts/ThemeContext';

// Components
import Header from '@/components/Header';
import Sidebar from '@/components/Sidebar';
import ErrorFallback from '@/components/ErrorBoundary';
import ToastProvider from '@/components/ToastProvider';
import SimpleTour from '@/components/SimpleTour';
import Onboarding from '@/components/Onboarding';
import OnboardingGuard from '@/components/OnboardingGuard';
import { OnboardingStorage } from '@/utils/onboarding.utils';

// Pages
import EssayCoach from '@/pages/EssayCoach';
import CollegeListBuilder from '@/pages/CollegeListBuilder';
import TextAnalyzer from '@/components/TextAnalyzer';
import AnalysisResultsPage from '@/pages/AnalysisResultsPage';
import PaymentPage from '@/components/PaymentPage';
import LoginPage from '@/features/auth/pages/LoginPage';
import SignupPage from '@/features/auth/pages/SignupPage';
import ForgotPasswordPage from '@/features/auth/pages/ForgotPasswordPage';
import EmailVerificationPage from '@/features/auth/pages/EmailVerificationPage';
import EmailVerificationSuccessPage from '@/features/auth/pages/EmailVerificationSuccessPage';
import EmailActionHandler from '@/features/auth/pages/EmailActionHandler';
import CustomEmailVerificationHandler from '@/features/auth/pages/CustomEmailVerificationHandler';

import AdminPanel from '@/pages/AdminPanel';
import Dashboard from '@/pages/Dashboard/NewDashboard';
import Resources from '@/pages/Resources';
import Deadlines from '@/pages/Deadlines';
import Messages from '@/pages/Messages';
import Applications from '@/pages/Applications';
import Progress from '@/pages/Progress';
import Settings from '@/pages/Settings';
import UploadDocuments from '@/pages/UploadDocuments';
import NewApplication from '@/pages/NewApplication';

// Hooks
import { useAuth } from '@/features/auth/context/AuthContext';


// Types
import { User } from '@/types';

type Theme = 'Light' | 'Dark';

// Create a client with optimized settings to reduce unnecessary refetches
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 60 * 1000, // 30 minutes - increased from 5 minutes
      gcTime: 60 * 60 * 1000, // 1 hour - increased from 10 minutes
      retry: 1, // Reduced from 3 to prevent excessive retries
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch on mount if data is fresh
      refetchOnReconnect: false, // Don't refetch on reconnect
    },
    mutations: {
      retry: 1,
    },
  },
});

interface PrivateRouteProps {
  user: User | null;
  loading: boolean;
  children: React.ReactNode;
}

const PrivateRoute: React.FC<PrivateRouteProps> = ({ user, loading, children }) => {
  const location = useLocation();
  
  // Show loading while checking authentication
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  
  return (
    <OnboardingGuard user={user}>
      {children}
    </OnboardingGuard>
  );
};

const AppContent: React.FC = () => {
  const navigate = useNavigate();
  const { user, loading, shouldShowTour, dismissTour, startTour } = useAuth();
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    if (user && !loading) {
      const shouldShowOnboardingFlow = OnboardingStorage.needsOnboarding(user);
      setShowOnboarding(shouldShowOnboardingFlow);
    }
  }, [user, loading]);

  useEffect(() => {
    if (user) {
      const needsOnboarding = OnboardingStorage.needsOnboarding(user);
      
      if (!needsOnboarding) {
        const shouldNavigate = localStorage.getItem('navigate_to_college_list');
        if (shouldNavigate === 'true' && window.location.pathname !== '/college-list-builder') {
          localStorage.removeItem('navigate_to_college_list');
          navigate('/college-list-builder');
        }
      }
    }
  }, [navigate, user]);

  const handleOnboardingComplete = () => {
    setShowOnboarding(false);
    setTimeout(() => {
      startTour();
      if (window.location.pathname !== '/college-list-builder') {
        navigate('/college-list-builder');
      }
    }, 500);
  };

  if(loading) 
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <div className="text-lg">Loading...</div>
        </div>
      </div>
    )

  if (user && showOnboarding) {
    return <Onboarding user={user} onComplete={handleOnboardingComplete} />;
  }
    

  return (
      <div className="h-screen flex bg-gradient-to-b from-gray-100 to-blue-50 dark:from-gray-900 dark:to-gray-800">
      {user && (
        <Sidebar user={user} />
      )}
      
      <div className="flex-1 flex flex-col main-content-wrapper" style={{ marginLeft: user ? 'var(--sidebar-width, 18rem)' : '0', transition: 'margin-left 0.3s ease' }}>
        {user && (
          <Header
            user={user}
            setUser={() => {}}
          />
        )}
        
        <main className="flex-1 min-h-0 overflow-y-auto h-full p-4">
          <Routes>
            <Route 
              path="/login" 
              element={user ? <Navigate to="/" replace /> : <LoginPage />} 
            />
            
            <Route 
              path="/signup" 
              element={user ? <Navigate to="/" replace /> : <SignupPage />} 
            />
            
            <Route 
              path="/forgot-password" 
              element={user ? <Navigate to="/" replace /> : <ForgotPasswordPage />} 
            />
            
            <Route 
              path="/verify-email" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <EmailVerificationPage />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/email-verification-success" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <EmailVerificationSuccessPage />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/auth/action" 
              element={<EmailActionHandler />} 
            />
            
            <Route 
              path="/verify-email-token" 
              element={<CustomEmailVerificationHandler />} 
            />
            

            
            <Route 
              path="/" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <Dashboard user={user!} />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/essay-coach" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <EssayCoach />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/college-list-builder" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <CollegeListBuilder />
                </PrivateRoute>
              } 
            />
            
            <Route
              path="/analyze/:university"
              element={
                <PrivateRoute user={user} loading={loading}>
                  <TextAnalyzer
                    
                  />
                </PrivateRoute>
              }
            />
            
            <Route
              path="/result/:university"
              element={
                <PrivateRoute user={user} loading={loading}>
                  <AnalysisResultsPage />
                </PrivateRoute>
              }
            />
            
            <Route 
              path="/buy-tokens" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <PaymentPage />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/admin" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <AdminPanel />
                </PrivateRoute>
              } 
            />
            
            {/* New routes for sidebar navigation */}
            <Route 
              path="/applications" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <Applications />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/applications/new" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <NewApplication />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/deadlines" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <Deadlines />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/messages" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <Messages />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/progress" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <Progress />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/settings" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <Settings />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/resources" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <Resources />
                </PrivateRoute>
              } 
            />
            
            <Route 
              path="/upload" 
              element={
                <PrivateRoute user={user} loading={loading}>
                  <UploadDocuments />
                </PrivateRoute>
              } 
            />
          </Routes>
        </main>
        
        {user && (
          <div className="fixed right-0 top-1/2 transform -translate-y-1/2 flex flex-col space-y-2 z-[9999]">
            <button
              className="report-issue-button bg-pink-300 hover:bg-pink-400 text-gray-800 px-1 py-6 rounded-l-lg transition-colors shadow-lg text-sm"
              onClick={() => window.open('https://airtable.com/appzqLGUk9kKjfji7/pagXjelFBnyc6AJGB/form', '_blank')}
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              Leave us your feedback 
            </button>

            <button
              className="contact-live-chat-button bg-blue-300 hover:bg-blue-400 text-gray-800 px-1 py-6 rounded-l-lg transition-colors shadow-lg text-sm"
              onClick={() => window.open('https://airtable.com/appzqLGUk9kKjfji7/pagvNFvimkwJYkQ0w/form', '_blank')}
              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
            >
              Get Live Consulting
            </button>
          </div>
        )}
        {user && (
          <SimpleTour isOpen={shouldShowTour} onClose={dismissTour} />
        )}
      </div>
      </div>
  );
};

// Get initial theme from localStorage or user preference - called once at module load
const getInitialTheme = (): Theme => {
  const savedTheme = localStorage.getItem('theme') as Theme | null;
  if (savedTheme) return savedTheme;
  
  // Check if user data is in localStorage
  const userDataStr = localStorage.getItem('user_onboarding_data');
  if (userDataStr) {
    try {
      const userData = JSON.parse(userDataStr);
      if (userData.theme_preference) {
        return userData.theme_preference as Theme;
      }
    } catch (e) {
      // Ignore parse errors
    }
  }
  
  return 'Light';
};

// Call once at module load to prevent re-evaluation
const INITIAL_THEME = getInitialTheme();

const App: React.FC = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ThemeProvider initialTheme={INITIAL_THEME}>
            <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
              <AppContent />
            </Router>
          </ThemeProvider>
          <ToastProvider />
          {/* Disabled to prevent websocket errors: {import.meta.env.NODE_ENV === 'development' && <ReactQueryDevtools />} */}
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App; 